/**
 * CL-JOURNEY-001A — Deterministic CRM ↔ Auth identity linking on invite signup.
 *
 * Governing audit: docs/CLIENT_PORTAL_JOURNEY_AUDIT.md
 */

import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { normalizePortalEmail } from "./clientPortalProfileResolve";

function safeStr(value) {
  return String(value ?? "").trim();
}

export const INVITE_IDENTITY_LINK_STATUS = Object.freeze({
  NO_MATCH: "no_match",
  LINK_REQUIRED: "link_required",
  ALREADY_LINKED: "already_linked",
  CONFLICTING_AUTH_UID: "conflicting_auth_uid",
  DUPLICATE_CRM_CONTACTS: "duplicate_crm_contacts",
});

export const INVITE_IDENTITY_LINK_CONFLICT = Object.freeze({
  DUPLICATE_CRM_CONTACTS: "duplicate_crm_contacts",
  CONFLICTING_AUTH_UID: "conflicting_auth_uid",
});

function isArchivedUserRow(row) {
  return safeStr(row?.status).toLowerCase() === "archived";
}

/**
 * CRM-style user rows matching invite email, excluding the canonical Auth profile doc.
 * @param {Array} candidateUserRows
 * @param {string} normalizedEmail
 * @param {string} authUid
 */
export function listCrmCandidatesForInviteSignup(candidateUserRows, normalizedEmail, authUid) {
  const em = normalizePortalEmail(normalizedEmail);
  const uid = safeStr(authUid);
  if (!em || !uid) return [];

  return (Array.isArray(candidateUserRows) ? candidateUserRows : []).filter((row) => {
    const rowEmail = normalizePortalEmail(row?.emailLower || row?.email);
    if (rowEmail !== em) return false;
    const rowId = safeStr(row?.id);
    if (!rowId || rowId === uid) return false;
    if (isArchivedUserRow(row)) return false;
    return true;
  });
}

/**
 * Pure resolver — no Firestore writes.
 * @param {{ normalizedEmail?: string, authUid?: string, candidateUserRows?: Array }} params
 */
export function resolveInviteSignupIdentityLink({
  normalizedEmail = "",
  authUid = "",
  candidateUserRows = [],
} = {}) {
  const em = normalizePortalEmail(normalizedEmail);
  const uid = safeStr(authUid);
  const candidates = listCrmCandidatesForInviteSignup(candidateUserRows, em, uid);

  const diagnostic = buildIdentityLinkDiagnostic({
    normalizedEmail: em,
    authUid: uid,
    matchedCrmContactId: null,
    matchedCrmContactAuthUid: null,
    candidateCount: candidates.length,
    status: INVITE_IDENTITY_LINK_STATUS.NO_MATCH,
    linkCreated: false,
    alreadyLinked: false,
    conflictType: null,
    warnings: [],
  });

  if (!em || !uid) {
    diagnostic.warnings = ["missing_email_or_auth_uid"];
    return diagnostic;
  }

  if (!candidates.length) {
    diagnostic.status = INVITE_IDENTITY_LINK_STATUS.NO_MATCH;
    return diagnostic;
  }

  if (candidates.length > 1) {
    diagnostic.status = INVITE_IDENTITY_LINK_STATUS.DUPLICATE_CRM_CONTACTS;
    diagnostic.conflictType = INVITE_IDENTITY_LINK_CONFLICT.DUPLICATE_CRM_CONTACTS;
    diagnostic.warnings = ["multiple_crm_contacts_same_email"];
    return diagnostic;
  }

  const crm = candidates[0];
  const crmId = safeStr(crm.id);
  diagnostic.matchedCrmContactId = crmId;
  diagnostic.matchedCrmContactAuthUid = safeStr(crm.authUid) || null;

  const existingAuthUid = safeStr(crm.authUid);
  if (!existingAuthUid) {
    diagnostic.status = INVITE_IDENTITY_LINK_STATUS.LINK_REQUIRED;
    return diagnostic;
  }

  if (existingAuthUid === uid) {
    diagnostic.status = INVITE_IDENTITY_LINK_STATUS.ALREADY_LINKED;
    diagnostic.alreadyLinked = true;
    return diagnostic;
  }

  diagnostic.status = INVITE_IDENTITY_LINK_STATUS.CONFLICTING_AUTH_UID;
  diagnostic.conflictType = INVITE_IDENTITY_LINK_CONFLICT.CONFLICTING_AUTH_UID;
  diagnostic.warnings = ["crm_contact_linked_to_different_auth_uid"];
  return diagnostic;
}

/**
 * @param {object} resolution
 */
export function buildIdentityLinkDiagnostic(resolution = {}) {
  return {
    normalizedEmail: normalizePortalEmail(resolution.normalizedEmail),
    authUid: safeStr(resolution.authUid),
    matchedCrmContactId: safeStr(resolution.matchedCrmContactId) || null,
    matchedCrmContactAuthUid: safeStr(resolution.matchedCrmContactAuthUid) || null,
    candidateCount: Number(resolution.candidateCount) || 0,
    status: safeStr(resolution.status) || INVITE_IDENTITY_LINK_STATUS.NO_MATCH,
    linkCreated: Boolean(resolution.linkCreated),
    alreadyLinked: Boolean(resolution.alreadyLinked),
    conflictType: safeStr(resolution.conflictType) || null,
    warnings: Array.isArray(resolution.warnings) ? [...resolution.warnings] : [],
  };
}

/**
 * Fields merged into users/{authUid} on invite signup (create — no self-update rules coupling).
 * @param {object} resolution
 */
export function buildAuthProfileIdentityLinkFields(resolution) {
  const status = safeStr(resolution?.status) || INVITE_IDENTITY_LINK_STATUS.NO_MATCH;
  const fields = {
    inviteIdentityLinkStatus: status,
  };

  if (resolution?.conflictType) {
    fields.inviteIdentityLinkConflictType = safeStr(resolution.conflictType);
  }

  const crmId = safeStr(resolution?.matchedCrmContactId);
  if (
    crmId &&
    (status === INVITE_IDENTITY_LINK_STATUS.LINK_REQUIRED ||
      status === INVITE_IDENTITY_LINK_STATUS.ALREADY_LINKED)
  ) {
    fields.linkedCrmContactId = crmId;
  }

  return fields;
}

/**
 * Write CRM contact authUid when link is required. Idempotent when CRM already linked to same uid.
 * @returns {Promise<object>} diagnostic with linkCreated / writeFailed flags
 */
export async function applyCrmContactAuthUidLink(db, crmContactId, authUid, existingCrmAuthUid = "") {
  const crmId = safeStr(crmContactId);
  const uid = safeStr(authUid);
  const linked = safeStr(existingCrmAuthUid);

  if (!crmId || !uid || crmId === uid) {
    return { ok: false, linkCreated: false, skipped: true };
  }
  if (linked && linked !== uid) {
    return { ok: false, linkCreated: false, skipped: true, reason: "conflicting_auth_uid" };
  }
  if (linked === uid) {
    return { ok: true, linkCreated: false, skipped: true, reason: "already_linked" };
  }

  if (!db) {
    return { ok: false, linkCreated: false, skipped: true, reason: "no_db" };
  }

  try {
    await updateDoc(doc(db, "users", crmId), {
      authUid: uid,
      updatedAt: serverTimestamp(),
    });
    return { ok: true, linkCreated: true, skipped: false };
  } catch {
    return { ok: false, linkCreated: false, skipped: false, reason: "write_failed" };
  }
}

/**
 * Apply CRM-side link after invite signup profile create.
 * Does not block signup on failure — caller surfaces warnings only.
 * @param {*} db
 * @param {object} resolution from resolveInviteSignupIdentityLink
 */
export async function applyInviteSignupIdentityLink(db, resolution) {
  const base = buildIdentityLinkDiagnostic(resolution);

  if (base.status === INVITE_IDENTITY_LINK_STATUS.ALREADY_LINKED) {
    return { ...base, linkCreated: false };
  }

  if (base.status !== INVITE_IDENTITY_LINK_STATUS.LINK_REQUIRED) {
    return base;
  }

  const writeResult = await applyCrmContactAuthUidLink(
    db,
    base.matchedCrmContactId,
    base.authUid,
    base.matchedCrmContactAuthUid
  );

  if (writeResult.linkCreated) {
    return { ...base, linkCreated: true };
  }

  if (writeResult.reason === "write_failed") {
    return {
      ...base,
      linkCreated: false,
      warnings: [...base.warnings, "crm_auth_uid_link_failed"],
    };
  }

  return base;
}
